import { CreateFedexPickupDTO } from "./fedex.types";

type BuildPickupParams = {
  date?: Date;
  start: string;
  end: string;
  country: string;
  state: string;
  city: string;
  cep: string;
  rua: string;
  numero: string;
  complemento?: string;
  telefone: string;
  personName: string;
};

const FEDEX_ACCOUNT = process.env.REACT_APP_FEDEX_ACCOUNT_NUMBER as string;

/** "18:00" -> "18:00:00". Já vindo com segundos, devolve como está. */
const comSegundos = (hora: string): string => {
  const s = String(hora ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return s;

  const pad = (n: string | number) => String(n).padStart(2, "0");
  return `${pad(m[1])}:${m[2]}:${m[3] ?? "00"}`;
};

/**
 * "2026-10-16T08:00:00-03:00" — hora local com o fuso junto.
 *
 * Era `toISOString()`, que devolve UTC: 08:00 daqui virava "11:00:00.000Z".
 * A FedEx passava a validar um horário que ninguém pediu, e o backend, que
 * guarda os caracteres 11..16 desse texto, gravava a coleta 3 horas adiantada
 * — por isso a lista mostrava 10:00 para quem digitou 07:00.
 */
const isoComFusoLocal = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");

  const offsetMin = -d.getTimezoneOffset();
  const sinal = offsetMin >= 0 ? "+" : "-";
  const offsetAbs = Math.abs(offsetMin);

  const data = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const hora = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const fuso = `${sinal}${pad(Math.floor(offsetAbs / 60))}:${pad(offsetAbs % 60)}`;

  return `${data}T${hora}${fuso}`;
};

export const buildFedexPickupDTO = ({
  date,
  start,
  end,
  country,
  state,
  city,
  cep,
  rua,
  numero,
  complemento,
  telefone,
  personName,
}: BuildPickupParams): CreateFedexPickupDTO => {
  if (!date) {
    throw new Error("Pickup date is required");
  }

  // Combina data + hora no formato ISO com timezone local
  const readyDate = new Date(date);

  const [hours, minutes, seconds] = start.split(":").map(Number);

  readyDate.setHours(hours, minutes, seconds ?? 0, 0);

  const readyDateTimestamp = isoComFusoLocal(readyDate);

  // SAME_DAY/FUTURE_DAY é sobre o DIA da coleta, não sobre a janela de horas.
  // Comparar "18:00" com "07:00" como string dava SAME_DAY para qualquer
  // agendamento, inclusive o de amanhã.
  const hoje = new Date();
  const mesmoDia =
    readyDate.getFullYear() === hoje.getFullYear() &&
    readyDate.getMonth() === hoje.getMonth() &&
    readyDate.getDate() === hoje.getDate();

  const pickupDateType = mesmoDia ? "SAME_DAY" : "FUTURE_DAY";

  return {
    associatedAccountNumber: {
      value: FEDEX_ACCOUNT,
    },
    originDetail: {
      pickupLocation: {
        contact: {
          personName: personName, // <- ideal vir do usuário logado futuramente
          phoneNumber: telefone,
        },
        address: {
          streetLines: [
            `${rua}, ${numero}${complemento ? ` - ${complemento}` : ""}`,
          ],
          city,
          stateOrProvinceCode: state,
          postalCode: cep,
          countryCode: country,
        },
      },
      packageLocation: "FRONT",
      readyDateTimestamp,
      // A FedEx exige HH:MM:SS aqui. O <input type="time"> emite "HH:MM"
      // quando o step é de minutos, e isso derruba o agendamento com
      // INCORRECT.COMPANYCLOSETIME.FORMAT.
      customerCloseTime: comSegundos(end),
      pickupDateType,
    },
    totalPackageCount: 1, // depois pode virar dinâmico
    totalWeight: {
      units: "KG",
      value: 1, // mesmo caso aqui
    },
    carrierCode: "FDXE",
  };
};
